// src/alertas/ConfigurarAlertas.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

export const ConfigurarAlertas = () => {
  const toast = useToast();

  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);

  // modal
  const [modalEditar, setModalEditar] = useState(false);
  const [alertaSeleccionada, setAlertaSeleccionada] = useState(null);

  // edición
  const [nuevoActivo, setNuevoActivo] = useState(false);
  const [nuevaHora, setNuevaHora] = useState("08:00");
  const [saving, setSaving] = useState(false);

  // =========================
  // Auth / rol
  // =========================
  const token = localStorage.getItem("token") || "";

  const rol = useMemo(() => {
    try {
      if (!token) return "empleado";
      const parts = token.split(".");
      if (parts.length < 2) return "empleado";
      const payloadStr = atob(parts[1]);
      const payload = JSON.parse(payloadStr || "{}");
      return payload?.rol || "empleado";
    } catch {
      return "empleado";
    }
  }, [token]);

  // =========================
  // Config: alertas disponibles
  // =========================
  const alertasDisponibles = useMemo(
    () => [
      {
        agente: "resumenJornadas",
        descripcion: "Resumen diario de jornadas",
        requiereHora: true,
        roles: ["admin", "gerente"],
      },
      {
        agente: "resumenIntervenciones",
        descripcion: "Resumen diario de intervenciones",
        requiereHora: true,
        roles: ["admin", "gerente"],
      },
      {
        agente: "fichajeTardio",
        descripcion: "Aviso por fichaje tardío",
        requiereHora: false,
        roles: ["admin", "gerente"],
      },
      {
        agente: "recordatorioFichaje",
        descripcion: "Recordatorio si no has fichado durante tu turno",
        requiereHora: false,
        roles: ["admin", "gerente", "usuario"],
      },
      {
        agente: "recordatorioPrevioTurno",
        descripcion: "Recordatorio antes de tu turno",
        requiereHora: false,
        roles: ["admin", "gerente", "usuario"],
      },
      {
        agente: "recordatorioFinTurno",
        descripcion: "Recordatorio al finalizar tu turno",
        requiereHora: false,
        roles: ["admin", "gerente", "usuario"],
      },
    ],
    []
  );

  const alertasFiltradas = useMemo(() => {
    return (alertasDisponibles || []).filter((a) => (a.roles || []).includes(rol));
  }, [alertasDisponibles, rol]);

  // =========================
  // API
  // =========================
  const cargarAlertas = async () => {
    if (!token) {
      setAlertas([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${Global.url}alertas`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        toast.error("No se pudieron cargar las alertas.");
        setAlertas([]);
        return;
      }

      const data = await res.json().catch(() => []);
      setAlertas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error al obtener alertas:", error);
      toast.error("Error de conexión al cargar alertas.");
      setAlertas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarAlertas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // Helpers (estado/hora por agente)
  // =========================
  const getAlertaActual = (agente) => (alertas || []).find((a) => a.agente === agente) || null;

  const estadoTxt = (agente) => (getAlertaActual(agente)?.activo ? "Activa" : "Desactivada");

  const horaTxt = (agente) => {
    const h = getAlertaActual(agente)?.hora_envio;
    return h ? String(h).slice(0, 5) : "-";
  };

  // =========================
  // Acciones
  // =========================
  const abrirModalEditar = (alerta) => {
    const actual = getAlertaActual(alerta.agente);

    setAlertaSeleccionada(alerta);
    setNuevoActivo(actual?.activo ?? false);
    setNuevaHora(actual?.hora_envio ? String(actual.hora_envio).slice(0, 5) : "08:00");

    setModalEditar(true);
  };

  const cerrarModalEditar = () => {
    if (saving) return;
    setModalEditar(false);
    setAlertaSeleccionada(null);
    setNuevoActivo(false);
    setNuevaHora("08:00");
  };

  const guardarCambios = async () => {
    if (!alertaSeleccionada?.agente) return;

    if (nuevoActivo && alertaSeleccionada.requiereHora && !/^\d{2}:\d{2}$/.test(nuevaHora || "")) {
      toast.warning("Introduce una hora válida (HH:MM).");
      return;
    }

    setSaving(true);
    try {
      const body = {
        agente: alertaSeleccionada.agente,
        activo: nuevoActivo,
        hora_envio: nuevoActivo && alertaSeleccionada.requiereHora ? nuevaHora : null,
      };

      const res = await fetch(`${Global.url}alertas`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "No se pudieron guardar las preferencias.");
        return;
      }

      toast.success("Preferencias guardadas.");
      await cargarAlertas();
      cerrarModalEditar();
    } catch (error) {
      console.error("Error al guardar alerta:", error);
      toast.error("Error de conexión al guardar las preferencias.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // DataGrid rows + columns
  // =========================
  const rows = useMemo(() => {
    return (alertasFiltradas || []).map((a) => ({
      id: a.agente,
      agente: a.agente,
      descripcion: a.descripcion,
      requiereHora: !!a.requiereHora, // ✅ necesario para mostrar selector de hora en el modal
      estado_txt: estadoTxt(a.agente),
      hora_txt: a.requiereHora ? horaTxt(a.agente) : "-",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertasFiltradas, alertas]);

  const columns = useMemo(
    () => [
      { field: "descripcion", headerName: "Alerta", flex: 1, minWidth: 280 },
      { field: "estado_txt", headerName: "Estado", minWidth: 160 },
      { field: "hora_txt", headerName: "Hora", minWidth: 120, sortable: false },
      {
        field: "acciones",
        headerName: "Acciones",
        sortable: false,
        minWidth: 180,
        renderCell: (params) => (
          <Button size="small" startIcon={<EditIcon />} onClick={() => abrirModalEditar(params.row)}>
            Editar
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertas, rol]
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Configuración de Alertas
        </Typography>

        <Paper sx={{ height: 560, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay alertas disponibles para tu rol."}
                </Box>
              ),
            }}
          />
        </Paper>

        {/* =========================
            Dialog: Editar alerta
           ========================= */}
        <Dialog open={modalEditar} onClose={cerrarModalEditar} fullWidth maxWidth="sm">
          <DialogTitle>Editar alerta</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {alertaSeleccionada?.descripcion || ""}
              </Typography>

              <FormControlLabel
                control={
                  <Switch checked={nuevoActivo} onChange={(e) => setNuevoActivo(e.target.checked)} />
                }
                label="Activar alerta"
              />

              {nuevoActivo && alertaSeleccionada?.requiereHora && (
                <TextField
                  label="Hora de envío"
                  type="time"
                  value={nuevaHora}
                  onChange={(e) => setNuevaHora(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarModalEditar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={guardarCambios} disabled={saving} startIcon={<SaveIcon />}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
